import React, { useState, useEffect } from "react";
import {
  sendLoggedGET,
  sendLoggedPOST,
} from "../../helpers/communityApiHelper";
import { Grid, Row, Col, FormControl, Button } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton.jsx";

const StaggeringSettings = () => {
  const [stores, setStores] = useState([]);
  const [mergerConfigs, setMergerConfigs] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedPartnerAppId, setSelectedPartnerAppId] = useState("");
  const [currentMergerConfig, setCurrentMergerConfig] = useState(null);
  const [storeConfigs, setStoreConfigs] = useState([]);
  const [maxNumbersOrders, setMaxNumbersOrders] = useState("");
  const [staggeringMinutes, setStaggeringMinutes] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await sendLoggedGET("/api/v1/mia/staggering");
        const data = response.data;
        setStores(data.partner_stores);
        setMergerConfigs(data.merger);

        if (data.partner_stores.length > 0) {
          setSelectedStoreId(data.partner_stores[0].id);
        }
      } catch (err) {
        setError("Failed to fetch settings: " + err.message);
        console.error("Failed to fetch settings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (selectedStoreId && mergerConfigs.length > 0) {
      const storeConfigs = mergerConfigs.filter(
        (config) => config.store_id === selectedStoreId
      );
      setStoreConfigs(storeConfigs);

      if (storeConfigs.length > 0) {
        // Auto-select first partner application if not already selected
        if (!selectedPartnerAppId || !storeConfigs.find(c => c.partner_application_id === selectedPartnerAppId)) {
          setSelectedPartnerAppId(storeConfigs[0].partner_application_id);
        }
      } else {
        setSelectedPartnerAppId("");
        setStoreConfigs([]);
      }
    }
  }, [selectedStoreId, mergerConfigs]);

  useEffect(() => {
    if (selectedStoreId && selectedPartnerAppId && mergerConfigs.length > 0) {
      const storeMergerConfig = mergerConfigs.find(
        (config) => config.store_id === selectedStoreId && config.partner_application_id === selectedPartnerAppId
      );
      setCurrentMergerConfig(storeMergerConfig);
      if (storeMergerConfig) {
        setMaxNumbersOrders(storeMergerConfig.staggering_max_orders_per_period);
        setStaggeringMinutes(storeMergerConfig.staggering_seconds / 60);
      } else {
        setMaxNumbersOrders("");
        setStaggeringMinutes("");
      }
    }
  }, [selectedStoreId, selectedPartnerAppId, mergerConfigs]);

  const handleUpdateSettings = async () => {
    if (!currentMergerConfig) {
      setError("No merger configuration selected.");
      return;
    }

    try {
      setMessage("");
      setError("");
      setLoading(true);

      const staggeringSecondsToSend = parseFloat(staggeringMinutes) * 60;
      const payload = {
        staggering_seconds: staggeringSecondsToSend,
        staggering_max_orders_per_period: parseInt(maxNumbersOrders, 10),
      };

      const endpoint = `/api/v1/mia/staggering/${currentMergerConfig.merger_config_id}`;
      const response = await sendLoggedPOST(endpoint, payload);

      if (response.data.success) {
        setMessage("Settings updated successfully!");
        setMergerConfigs((prevConfigs) =>
          prevConfigs.map((config) =>
            config.merger_config_id ===
            response.data.merger_config.merger_config_id
              ? response.data.merger_config
              : config
          )
        );
      } else {
        setError(
          "Failed to update settings: " +
            (response.data.message || "Unknown error")
        );
      }
    } catch (err) {
      setError("Failed to update settings: " + err.message);
      console.error("Failed to update settings:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading settings...</div>;
  }

  return (
    <div style={{ padding: "20px" }}>
      {message && (
        <div style={{ color: "green", marginBottom: "10px" }}>{message}</div>
      )}
      {error && (
        <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>
      )}

      {stores.length === 0 && !loading && (
        <p>No partner stores available to configure.</p>
      )}

      {stores.length > 0 && (
        <Grid>
          <Row>
            <Col md={12}>
              <div style={{ marginBottom: "15px" }}>
                <label htmlFor="store-select" style={{ marginRight: "10px" }}>
                  Select Store:
                </label>
                <FormControl
                  componentClass="select"
                  id="store-select"
                  value={selectedStoreId}
                  onChange={(e) =>
                    setSelectedStoreId(parseInt(e.target.value, 10))
                  }
                  style={{ width: "auto", display: "inline-block" }}
                >
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name} ({store.address})
                    </option>
                  ))}
                </FormControl>
              </div>
            </Col>
            <Col md={12}>
              <div style={{ marginBottom: "15px" }}>
                <label htmlFor="partner-app-select" style={{ marginRight: "10px" }}>
                  Select Partner Application:
                </label>
                <FormControl
                  componentClass="select"
                  id="partner-app-select"
                  value={selectedPartnerAppId}
                  onChange={(e) =>
                    setSelectedPartnerAppId(parseInt(e.target.value, 10))
                  }
                  style={{ width: "auto", display: "inline-block" }}
                  disabled={storeConfigs.length === 0}
                >
                  {storeConfigs.map((config) => (
                    <option key={config.merger_config_id} value={config.partner_application_id}>
                      {config.partner_application_name || `Partner App ID: ${config.partner_application_id}`}
                    </option>
                  ))}
                </FormControl>
              </div>
            </Col>
          </Row>

          {currentMergerConfig ? (
            <Row>
              <Col md={6}>
                <div style={{ marginBottom: "10px" }}>
                  <label htmlFor="max-orders" style={{ marginRight: "10px" }}>
                    Max Number of Orders per staggering period:
                  </label>
                  <FormControl
                    id="max-orders"
                    type="number"
                    value={maxNumbersOrders}
                    onChange={(e) => setMaxNumbersOrders(e.target.value)}
                    min="0"
                    style={{ width: "auto", display: "inline-block" }}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div style={{ marginBottom: "20px" }}>
                  <label
                    htmlFor="staggering-minutes"
                    style={{ marginRight: "10px" }}
                  >
                    Staggering period (in minutes):
                  </label>
                  <FormControl
                    id="staggering-minutes"
                    type="number"
                    value={staggeringMinutes}
                    onChange={(e) => setStaggeringMinutes(e.target.value)}
                    min="0"
                    style={{ width: "auto", display: "inline-block" }}
                  />
                </div>
              </Col>
              <Col md={12}>
                <CustomButton
                  bsStyle="info"
                  fill
                  onClick={handleUpdateSettings}
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update Settings"}
                </CustomButton>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col md={12}>
                <p>No merger configuration found for the selected store.</p>
              </Col>
            </Row>
          )}
        </Grid>
      )}
    </div>
  );
};

export default StaggeringSettings;
